import { ModalContextType } from './modal-context.type';
import { ModalStackItem } from './modal-stack-item.interface';
import React, { createContext, useContext, useState, ReactNode } from 'react';
import Modal from 'src/components/Modal/Modal';
import i18n from 'src/i18n';

// Create the context with undefined as initial value
const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Custom hook to use the modal context
export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error(i18n.t('ERRORS.CONTEXT.MODAL'));
  }
  return context;
};

// ModalProvider component to wrap the app and provide modal functionality
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // State to keep track of all open modals in a stack
  const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);

  // Function to open a new modal by adding it to the stack
  const openModal = (
    content: ReactNode,
    className: string,
    id: string,
    overlayClassName?: string,
    modalBodyClassName?: string
  ) => {
    setModalStack((prevStack) => [...prevStack, { content, className, id, overlayClassName, modalBodyClassName }]);
  };

  // Function to close the topmost modal (LIFO stack behavior)
  const closeModal = () => {
    setModalStack((prevStack) => prevStack.slice(0, -1));
  };

  // Function to update the className of a specific modal based on its ID
  const updateModalClassName = React.useCallback((id: string, className: string) => {
    setModalStack((prevStack) => prevStack.map((modal) => (modal.id === id ? { ...modal, className } : modal)));
  }, []);

  return (
    <ModalContext.Provider value={{ openModal, closeModal, updateModalClassName }}>
      {children}
      {/* Renders all modals in the stack */}
      {modalStack.map((modal, index) => {
        const zIndexClass = `z-index-${1000 + index}`;
        return (
          <Modal
            key={modal.id}
            isOpen={true}
            onClose={closeModal}
            overlayClassName={modal.overlayClassName}
            className={`${modal.className} ${zIndexClass}`}
            modalBodyClassName={modal.modalBodyClassName}
          >
            {modal.content}
          </Modal>
        );
      })}
    </ModalContext.Provider>
  );
};
